import React from 'react';
import usePageMetadata from '../utils/usePageMetadata';
import './TermsOfService.css';

const TermsOfService = () => {
  usePageMetadata({
    title: 'Terms of Service | The Ad Shop',
    description:
      'Review the terms that govern your use of The Ad Shop, including account eligibility, subscription billing, data usage, and acceptable conduct.',
    canonicalUrl: 'https://theadshop.co/termsofservice',
    meta: [
      { property: 'og:title', content: 'Terms of Service | The Ad Shop' },
      {
        property: 'og:description',
        content:
          'Understand how The Ad Shop operates, how billing and subscriptions are managed, and your responsibilities while using the platform.',
      },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: 'https://theadshop.co/termsofservice' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Terms of Service | The Ad Shop' },
      {
        name: 'twitter:description',
        content:
          'Learn about account eligibility, acceptable use, billing, data handling, and termination policies for The Ad Shop.',
      },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'TermsOfService',
      name: 'The Ad Shop Terms of Service',
      url: 'https://theadshop.co/termsofservice',
      description:
        'Terms and conditions covering subscription plans, site limits, data usage, and user responsibilities for The Ad Shop platform.',
      publisher: {
        '@type': 'Organization',
        name: 'The Media Shop, LLC',
        url: 'https://theadshop.co/',
      },
      inLanguage: 'en-US',
      datePublished: '2025-11-11',
      dateModified: '2025-11-11',
    },
  });

  return (
    <div className="terms-page">
      <div className="terms-container">
        <header className="terms-header">
          <h1>Terms of Service</h1>
          <p className="effective-date">Effective Date: November 11, 2025</p>
          <p className="intro">
            The Ad Shop is a platform operated by The Media Shop, LLC (&quot;The Media Shop,&quot;
            &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) that helps publishers unify analytics,
            compliance, and revenue insights across their properties. By accessing or using The Ad Shop
            (the &quot;Service&quot;), you agree to be bound by these Terms of Service (&quot;Terms&quot;).
            If you do not agree, do not use the Service.
          </p>
        </header>

        <div className="terms-content">
          <section className="terms-section">
            <h2>1. Eligibility & Accounts</h2>
            <p>
              You must be at least 18 years old and able to form a binding contract to use the Service.
              You are responsible for maintaining the confidentiality of your account credentials and for
              all activities that occur under your account.
            </p>
            <p>
              You agree to provide accurate, current, and complete information when creating your account
              and to update such information as needed. We reserve the right to suspend or terminate your
              account if any information provided is inaccurate, misleading, or violates these Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Subscription Plans & Site Limits</h2>
            <p>
              The Service offers tiered subscription plans (e.g., Free, Basic, Pro, Ultimate). Each plan
              includes a specific maximum number of websites you may connect to your account. Exceeding
              plan limits may require upgrading your subscription. Plan details, including current pricing
              and site allowances, are available within the admin dashboard.
            </p>
            <p>
              You authorize us (and our payment processors) to charge you for any paid plan you select.
              All fees are non-refundable except where prohibited by law or expressly stated otherwise. If
              you downgrade or cancel your plan, changes take effect at the end of the current billing
              cycle unless otherwise specified.
            </p>
          </section>

          <section className="terms-section">
            <h2>3. Billing, Taxes & Trials</h2>
            <p>
              Paid plans are billed in advance on a recurring basis (monthly or annually). Prices do not
              include taxes, levies, or duties imposed by taxing authorities; you are responsible for
              paying all such amounts. If you participate in a free trial or promotional offer, the
              applicable billing terms will be described in the offer.
            </p>
            <p>
              We may change plan prices with advance notice to you. Continued use of the Service after the
              price change takes effect constitutes acceptance of the new price. If you do not agree, you
              may cancel your subscription before the change takes effect.
            </p>
          </section>

          <section className="terms-section">
            <h2>4. Use of the Service</h2>
            <p>
              You agree to use the Service only for lawful purposes and in accordance with these Terms. You
              will not:
            </p>
            <ul>
              <li>Interfere with or disrupt the integrity or performance of the Service;</li>
              <li>
                Attempt to gain unauthorized access to the Service, related systems, or networks;
              </li>
              <li>
                Misuse data obtained through connected integrations (e.g., Google Analytics, AdSense);
              </li>
              <li>
                Use the Service to upload, transmit, or execute malicious code or abusive content;
              </li>
              <li>Reverse engineer, decompile, or otherwise attempt to derive source code.</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate your access to the Service if we determine that
              your use violates these Terms or poses a risk to other users or the integrity of the platform.
            </p>
          </section>

          <section className="terms-section">
            <h2>5. Integrations & Third-Party Services</h2>
            <p>
              The Service provides optional integrations with third-party services (e.g., Google Analytics,
              Google AdSense, Google Search Console). By connecting a third-party account, you authorize us
              to access data from that service on your behalf solely to deliver the requested features.
            </p>
            <p>
              We do not control third-party services and are not responsible for their content or
              performance. Your use of third-party services is governed by their respective terms and
              privacy policies. You may disconnect integrations at any time from your account settings.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Data & Privacy</h2>
            <p>
              Our Privacy Policy explains how we collect, use, store, and share information. By using the
              Service, you consent to our data practices as outlined in the policy. You are responsible
              for obtaining any necessary consents from your users or stakeholders before connecting their
              data sources to our platform.
            </p>
            <p>
              You retain ownership of the data you upload or connect through the Service. You grant us a
              limited, non-exclusive license to process that data solely for the purpose of providing,
              maintaining, and improving the Service.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Intellectual Property</h2>
            <p>
              The Service, including its content, features, and functionality, is owned by The Media Shop,
              LLC and its licensors. The Ad Shop name, logo, and other trademarks may not be used without
              our prior written consent. You may not copy, modify, or create derivative works of the
              Service or any part thereof.
            </p>
            <p>
              You may submit feedback, suggestions, or ideas. By doing so, you grant us an irrevocable,
              worldwide, royalty-free license to use, modify, and incorporate such feedback into the
              Service without obligation.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES
              OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED
              WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
            <p>
              We do not guarantee that the Service will be uninterrupted, timely, secure, or error-free, or
              that the results obtained from the use of the Service will be accurate or reliable.
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL THE MEDIA SHOP, LLC, ITS
              AFFILIATES, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR
              INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
            </p>
            <ul>
              <li>Your use or inability to use the Service;</li>
              <li>Any unauthorized access to or use of our servers and/or any personal information;</li>
              <li>Any interruption or cessation of transmission to or from the Service;</li>
              <li>Any bugs, viruses, or the like transmitted to or through the Service by any third party;</li>
              <li>Any content errors or omissions.</li>
            </ul>
            <p>
              Our aggregate liability for all claims related to the Service shall not exceed the amounts
              paid by you to us in the 12 months preceding the event giving rise to the claim.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless The Media Shop, LLC, its affiliates, officers,
              directors, employees, and agents from any claims, liabilities, damages, judgments, awards,
              losses, costs, expenses, or fees (including reasonable attorneys&apos; fees) arising out of or
              relating to your violation of these Terms or your use of the Service.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without notice,
              for conduct that violates these Terms or that we determine is harmful to other users, us, or
              third parties. Upon termination, your right to use the Service will cease immediately. You
              may cancel your subscription or delete your account at any time from your dashboard.
            </p>
          </section>

          <section className="terms-section">
            <h2>12. Changes to the Service or Terms</h2>
            <p>
              We may modify or discontinue the Service at any time without liability. We may also update
              these Terms from time to time. When we do, we will update the effective date at the top of
              the page and, where appropriate, provide notice within the Service. Your continued use of
              the Service after any changes become effective constitutes acceptance of those changes.
            </p>
          </section>

          <section className="terms-section">
            <h2>13. Governing Law & Venue</h2>
            <p>
              These Terms are governed by the laws of the State of North Carolina, without regard to its
              conflict of law principles. Any disputes arising out of or related to the Service shall be
              filed exclusively in the state or federal courts located in Mecklenburg County, North
              Carolina, USA.
            </p>
          </section>

          <section className="terms-section">
            <h2>14. Contact Us</h2>
            <p>
              If you have any questions about these Terms or the Service, please contact us at{' '}
              <a href="mailto:support@theadshop.co">support@theadshop.co</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;


















